import {
  getHeaderImage,
  MCMXCIV_LOGO,
  eventTitle as getEventTitle,
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  getSessionDateLabel,
  getSessionTimeLabel,
  formatDateParts,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import ButtonWidget from './widgets/button-widget';
import FooterWidget from './widgets/footer-widget';
import LogoWidget from './widgets/logo-widget';
import { templateStaticDefaults } from './template-defaults';
import { BlockRenderer } from './widgets/block-renderer';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';
import { BLOCK_ID } from './widgets/block-ids';

const DEFAULTS = templateStaticDefaults.sterling;

const STERLING_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.headerLogo]: { height: 24, align: 'center', background: 'var(--container)', padding: '20px' },
  [BLOCK_ID.mainImage]: {
    width: 536,
    height: 280,
    borderRadius: 'var(--image-radius)',
  },
  [BLOCK_ID.salutation]: {
    color: 'var(--button-text)',
    fontFamily: 'var(--heading-font)',
    fontSize: '40px',
    fontWeight: 700,
    lineHeight: '44px',
    textAlign: 'center',
    background: 'var(--accent)',
    padding: '32px 56px 0 56px',
  },
  [BLOCK_ID.title]: {
    color: 'var(--button-text)',
    fontFamily: 'var(--body-font)',
    fontSize: '27px',
    fontWeight: 400,
    lineHeight: '32px',
    textAlign: 'center',
    background: 'var(--accent)',
    padding: '20px 56px 32px 56px',
  },
  [BLOCK_ID.description]: {
    color: 'var(--accent)',
    fontFamily: 'var(--body-font)',
    fontSize: '20px',
    lineHeight: '28px',
    background: 'var(--secondary-bg)',
    padding: '32px 56px',
  },
  [BLOCK_ID.dateTimeCard]: {
    accentColor: 'var(--body)',
    headingColor: 'var(--body)',
    labelFontWeight: 700,
    borderColor: 'var(--accent)',
    dividerColor: 'rgba(10,16,13,0.1)',
    background: 'var(--secondary-bg)',
    padding: '12px 56px 0 56px',
  },
  [BLOCK_ID.cta]: {
    size: 'large',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    background: 'var(--secondary-bg)',
    fontFamily: 'var(--heading-font)',
    fontWeight: 400,
    padding: '20px 56px',
  },
  [BLOCK_ID.legalText]: {
    color: 'var(--accent)',
    fontFamily: 'var(--body-font)',
    fontSize: '12px',
    lineHeight: '18px',
    textAlign: 'center',
    background: 'var(--container)',
    padding: '48px 64px',
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

export const Sterling = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const secondaryBg = resolveThemedColor(
    customizations?.secondaryBackgroundColor,
    themeColors?.secondaryBackgroundColor,
    DEFAULTS.secondaryBackgroundColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const secondaryAccent = resolveThemedColor(
    customizations?.secondaryAccentColor,
    themeColors?.secondaryAccentColor,
    DEFAULTS.secondaryAccentColor
  );
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '0px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '0px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || MCMXCIV_LOGO;
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    'Tilt Warp, sans-serif'
  );
  const bodyFont = resolveThemedFont(customizations?.bodyFont, theme?.font_pack?.body_name, 'Mona Sans, sans-serif');
  const _salutation = salutation ?? (customizations?.salutation || DEFAULTS.salutation);
  const eventTitle = title ?? (customizations?.title || getEventTitle(event, 'en'));
  const eventDescription = description || customizations?.description || DEFAULTS.description;
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = bg;
  const footerTextColor = accent;
  // sterling's footer sits on its page background, so its card takes the container instead
  const footerBackground = container;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      secondaryAccent,
      secondaryBg,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: STERLING_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <table
        data-component="Sterling"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--secondary-bg': secondaryBg,
            '--accent': accent,
            '--button-color': buttonColor,
            '--secondary-accent': secondaryAccent,
            '--heading': heading,
            '--body': body,
            '--button-text': buttonText,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  <tr>
                    <td>
                      {blocks.map((block) => (
                        <BlockShell
                          key={block.id}
                          block={block}
                          isEditMode={!!isEditMode}
                          isFirst={block.id === blocks[0]?.id}
                          isLast={block.id === blocks[blocks.length - 1]?.id}
                          selected={block.id === selectedBlockId}
                          onSelect={onSelectBlock}
                          onDelete={onDeleteBlock}
                          onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                          onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                        >
                          <BlockRenderer
                            block={block}
                            stylePreset={STERLING_BLOCK_PRESETS[block.id]}
                            onValueChange={onUpdateBlockValue}
                            onImageChange={onUpdateBlockImage}
                            context={blockContext}
                          />
                        </BlockShell>
                      ))}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td>
                        <CalendarLinksWidget links={calendarLinks} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Sterling"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--secondary-bg': secondaryBg,
          '--accent': accent,
          '--button-color': buttonColor,
          '--secondary-accent': secondaryAccent,
          '--heading': heading,
          '--body': body,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                <tr>
                  <td>
                    <LogoWidget
                      src={headerImg}
                      setImage={setHeaderImage}
                      height={24}
                      align="center"
                      background="var(--container)"
                      padding="20px"
                    />

                    <ImageWidget
                      src={bodyImg}
                      setImage={setMainImage}
                      width={536}
                      height={280}
                      borderRadius={imageRadius}
                    />

                    <HeaderWidget
                      value={_salutation}
                      setValue={setSalutation}
                      color="var(--button-text)"
                      fontFamily="var(--heading-font)"
                      fontSize="40px"
                      fontWeight={700}
                      lineHeight="44px"
                      textAlign="center"
                      background="var(--accent)"
                      padding="32px 56px 0 56px"
                    />
                    <HeaderWidget
                      value={eventTitle}
                      setValue={setTitle}
                      color="var(--button-text)"
                      fontFamily="var(--body-font)"
                      fontSize="27px"
                      fontWeight={400}
                      lineHeight="32px"
                      textAlign="center"
                      background="var(--accent)"
                      padding="20px 56px 32px 56px"
                    />

                    <ParagraphWidget
                      value={eventDescription}
                      setValue={setDescription}
                      color="var(--accent)"
                      fontFamily="var(--body-font)"
                      fontSize="20px"
                      lineHeight="28px"
                      background="var(--secondary-bg)"
                      padding="32px 56px"
                    />

                    {sessionTimestamp && (
                      <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                        <tbody>
                          <tr>
                            <td style={{ background: 'var(--secondary-bg)', padding: '12px 56px 0 56px' }}>
                              <table
                                width="100%"
                                cellPadding={0}
                                cellSpacing={0}
                                border={0}
                                role="presentation"
                                style={{ border: '1px solid var(--accent)' }}
                              >
                                <tbody>
                                  <tr>
                                    <td style={{ padding: '20px', borderBottom: '1px solid rgba(10,16,13,0.1)' }}>
                                      <table
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        role="presentation"
                                      >
                                        <tbody>
                                          <tr>
                                            <td
                                              style={{
                                                color: 'var(--body)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 700,
                                              }}
                                            >
                                              Date
                                            </td>
                                            <td
                                              align="right"
                                              style={{
                                                color: 'var(--body)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 400,
                                              }}
                                            >
                                              {sessionDateLabel}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td style={{ padding: '20px' }}>
                                      <table
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        role="presentation"
                                      >
                                        <tbody>
                                          <tr>
                                            <td
                                              style={{
                                                color: 'var(--body)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 700,
                                              }}
                                            >
                                              Time
                                            </td>
                                            <td
                                              align="right"
                                              style={{
                                                color: 'var(--body)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 400,
                                              }}
                                            >
                                              {sessionTimeLabel}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}

                    <ButtonWidget
                      value={_cta}
                      setValue={setCta}
                      href={anchorHref}
                      size="large"
                      color="var(--button-text)"
                      buttonColor="var(--button-color)"
                      background="var(--secondary-bg)"
                      fontFamily="var(--heading-font)"
                      fontWeight={400}
                      padding="20px 56px"
                      borderRadius={buttonRadius}
                    />

                    <ParagraphWidget
                      value={DEFAULTS.complianceText}
                      color="var(--accent)"
                      fontFamily="var(--body-font)"
                      fontSize="12px"
                      lineHeight="18px"
                      textAlign="center"
                      background="var(--container)"
                      padding="48px 64px"
                    />
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--secondary-accent)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--secondary-accent)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--secondary-accent)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--secondary-accent)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--secondary-accent)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
